import type { OntologyBlock } from "../../api/admin-client.ts"
import type { GraphNode } from "./types.ts"

interface NodeDetailPanelProps {
	readonly node: GraphNode | null
	readonly onExpand: (node: GraphNode) => void
	/**
	 * 카탈로그 블럭 — node.kind 와 매칭되는 블럭의 설명/아이콘/example 을 역참조해
	 * "이 kind 는 이런 구조로 쓰입니다" 힌트 섹션을 렌더한다. 서비스 상세 Graph 탭이 주입.
	 */
	readonly blocks?: ReadonlyArray<OntologyBlock>
}

/**
 * 그래프 노드 상세 패널.
 *
 * 스타일은 `components.css` 의 `.node-detail*` 클래스 세트로 분리해 섹션간 여백/타이포를 통일한다.
 * (인라인 style 난발로 빡빡하던 2026-04 #피드백 해결.)
 */
export function NodeDetailPanel({ node, onExpand, blocks }: NodeDetailPanelProps) {
	if (!node) {
		return (
			<div className="node-detail__empty">
				그래프에서 노드를 클릭하면 상세가 여기에 표시됩니다. 더블클릭 시 1홉 이웃이 확장됩니다.
			</div>
		)
	}

	const parsedMetadata = (() => {
		if (!node.metadata) return undefined
		try {
			return JSON.parse(node.metadata) as Record<string, unknown>
		} catch {
			return undefined
		}
	})()

	// kind 로 블럭 카탈로그 역참조 — 블럭 = kind 1:1 원칙상 첫 kind 매칭만 보면 된다.
	const block = blocks?.find((b) => b.slice.kinds.some((k) => k.name === node.kind))
	const example = block?.example

	return (
		<div className="node-detail">
			<div>
				<div className="node-detail__eyebrow">
					{node.domain} · {node.kind}
				</div>
				<h3 className="node-detail__title">{node.name || "(이름 없음)"}</h3>
				<code className="inline node-detail__id">{node.id}</code>
			</div>

			{node.content && (
				<div className="node-detail__section">
					<div className="node-detail__label">내용</div>
					<div className="node-detail__content">{node.content}</div>
				</div>
			)}

			{parsedMetadata && Object.keys(parsedMetadata).length > 0 && (
				<div className="node-detail__section">
					<div className="node-detail__label">속성</div>
					<pre className="node-detail__pre">{JSON.stringify(parsedMetadata, null, 2)}</pre>
				</div>
			)}

			{block && (
				<div className="node-detail__section">
					<div className="node-detail__label">블럭 설명</div>
					<div className="node-detail__block-head">
						<span aria-hidden>{block.icon}</span>
						<strong>{block.displayName}</strong>
						<span className="muted">({block.category})</span>
					</div>
					<div className="node-detail__desc">{block.description}</div>
				</div>
			)}

			{example && (
				<div className="node-detail__section">
					<div className="node-detail__label">등록된 예시</div>
					{example.summary && <div className="node-detail__desc">{example.summary}</div>}
					<div className="node-detail__example-node">
						<div className="node-detail__example-head">
							<code className="inline">{example.node.kind}</code>
							<strong>{example.node.displayName ?? example.node.name}</strong>
						</div>
						{example.node.description && <div className="node-detail__desc">{example.node.description}</div>}
					</div>
					{example.edges && example.edges.length > 0 && (
						<ul className="node-detail__example-list">
							{example.edges.map((edge, idx) => (
								<li
									key={`${edge.from.kind}|${edge.from.name}|${edge.relation}|${edge.to.kind}|${edge.to.name}`}
									className="node-detail__example-edge"
								>
									<div className="node-detail__example-head">
										<span className="node-detail__example-edge-index">관계 {idx + 1}</span>
										<code className="inline">{edge.from.kind}</code>
										<span className="node-detail__example-edge-arrow">—</span>
										<code className="inline" style={{ fontWeight: 600 }}>
											{edge.relation}
										</code>
										<span className="node-detail__example-edge-arrow">→</span>
										<code className="inline">{edge.to.kind}</code>
									</div>
								</li>
							))}
						</ul>
					)}
				</div>
			)}

			<button
				type="button"
				className="btn btn-primary btn-sm node-detail__expand-btn"
				onClick={() => onExpand(node)}
			>
				이 노드 1홉 확장
			</button>
		</div>
	)
}
